import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getShipmentByTracking, getClearanceFee } from '../lib/firestoreService';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const STEPS = [
  { label: 'Order Confirmed', icon: '📋' },
  { label: 'Picked Up',       icon: '📦' },
  { label: 'On The Way',      icon: '✈️'  },
  { label: 'Arrival',         icon: '🛬'  },
  { label: 'Custom Hold',     icon: '🔒'  },
  { label: 'Delivered',       icon: '✅'  },
];

const STATUS_COLORS = {
  'Order Confirmed': '#6366f1',
  'Picked':          '#f59e0b',
  'Picked Up':       '#f59e0b',
  'On The Way':      '#3b82f6',
  'Arrival':         '#8b5cf6',
  'Custom Hold':     '#ef4444',
  'Delivered':       '#10b981',
};

function geocode(place) {
  const map = {
    // ── United Kingdom ─────────────────────────────────────
    london:          [51.5074,  -0.1278],
    manchester:      [53.4808,  -2.2426],
    birmingham:      [52.4862,  -1.8904],
    glasgow:         [55.8642,  -4.2518],
    edinburgh:       [55.9533,  -3.1883],
    liverpool:       [53.4084,  -2.9916],
    bristol:         [51.4545,  -2.5879],
    leeds:           [53.8008,  -1.5491],

    // ── Western Europe ──────────────────────────────────────
    paris:           [48.8566,   2.3522],
    berlin:          [52.5200,  13.4050],
    amsterdam:       [52.3676,   4.9041],
    brussels:        [50.8503,   4.3517],
    madrid:          [40.4168,  -3.7038],
    barcelona:       [41.3851,   2.1734],
    rome:            [41.9028,  12.4964],
    milan:           [45.4654,   9.1859],
    vienna:          [48.2082,  16.3738],
    zurich:          [47.3769,   8.5417],
    geneva:          [46.2044,   6.1432],
    lisbon:          [38.7169,  -9.1395],
    dublin:          [53.3498,  -6.2603],
    oslo:            [59.9139,  10.7522],
    stockholm:       [59.3293,  18.0686],
    copenhagen:      [55.6761,  12.5683],
    helsinki:        [60.1699,  24.9384],
    athens:          [37.9838,  23.7275],
    frankfurt:       [50.1109,   8.6821],
    hamburg:         [53.5753,  10.0153],
    munich:          [48.1351,  11.5820],
    prague:          [50.0755,  14.4378],
    warsaw:          [52.2297,  21.0122],
    budapest:        [47.4979,  19.0402],
    bucharest:       [44.4268,  26.1025],
    zagreb:          [45.8150,  15.9819],
    luxembourg:      [49.6116,   6.1319],
    rotterdam:       [51.9225,   4.4792],
    antwerp:         [51.2194,   4.4025],
    nice:            [43.7102,   7.2620],
    lyon:            [45.7640,   4.8357],
    marseille:       [43.2965,   5.3698],
    seville:         [37.3891,  -5.9845],
    valencia:        [39.4699,  -0.3763],
    porto:           [41.1579,  -8.6291],
    palermo:         [38.1157,  13.3615],
    naples:          [40.8518,  14.2681],
    florence:        [43.7696,  11.2558],
    venice:          [45.4408,  12.3155],
    bern:            [46.9480,   7.4474],
    basel:           [47.5596,   7.5886],
    reykjavik:       [64.1355, -21.8954],
    tallinn:         [59.4370,  24.7536],
    riga:            [56.9496,  24.1052],
    vilnius:         [54.6872,  25.2797],
    bratislava:      [48.1486,  17.1077],
    sofia:           [42.6977,  23.3219],
    belgrade:        [44.8176,  20.4569],
    sarajevo:        [43.8563,  18.4131],
    skopje:          [41.9973,  21.4280],
    tirana:          [41.3275,  19.8187],
    podgorica:       [42.4304,  19.2594],
    valletta:        [35.8997,  14.5147],
    nicosia:         [35.1856,  33.3823],
    chisinau:        [47.0105,  28.8638],

    // ── North America ───────────────────────────────────────
    'new york':      [40.7128, -74.0060],
    'los angeles':   [34.0522,-118.2437],
    chicago:         [41.8781, -87.6298],
    houston:         [29.7604, -95.3698],
    phoenix:         [33.4484,-112.0740],
    philadelphia:    [39.9526, -75.1652],
    'san antonio':   [29.4241, -98.4936],
    'san diego':     [32.7157,-117.1611],
    dallas:          [32.7767, -96.7970],
    'san jose':      [37.3382,-121.8863],
    austin:          [30.2672, -97.7431],
    jacksonville:    [30.3322, -81.6557],
    'fort worth':    [32.7555, -97.3308],
    columbus:        [39.9612, -82.9988],
    charlotte:       [35.2271, -80.8431],
    'san francisco': [37.7749,-122.4194],
    indianapolis:    [39.7684, -86.1581],
    seattle:         [47.6062,-122.3321],
    denver:          [39.7392,-104.9903],
    washington:      [38.9072, -77.0369],
    nashville:       [36.1627, -86.7816],
    'oklahoma city': [35.4676, -97.5164],
    'el paso':       [31.7619,-106.4850],
    boston:          [42.3601, -71.0589],
    portland:        [45.5051,-122.6750],
    'las vegas':     [36.1699,-115.1398],
    miami:           [25.7617, -80.1918],
    atlanta:         [33.7490, -84.3880],
    minneapolis:     [44.9778, -93.2650],
    toronto:         [43.6532, -79.3832],
    montreal:        [45.5017, -73.5673],
    vancouver:       [49.2827,-123.1207],
    calgary:         [51.0447,-114.0719],
    ottawa:          [45.4215, -75.6972],
    'mexico city':   [19.4326, -99.1332],
    guadalajara:     [20.6597,-103.3496],
    monterrey:       [25.6866, -100.3161],
    panama:          [ 8.9936, -79.5197],
    'san jose cr':   [ 9.9281, -84.0907],
    'santo domingo': [18.4861, -69.9312],
    havana:          [23.1136, -82.3666],
    'san juan':      [18.4655, -66.1057],
    kingston:        [17.9970, -76.7936],
    guatemala:       [14.6349, -90.5069],

    // ── South America ───────────────────────────────────────
    'sao paulo':     [-23.5505, -46.6333],
    'rio de janeiro':[-22.9068, -43.1729],
    brasilia:        [-15.8267, -47.9218],
    buenos:          [-34.6037, -58.3816],
    'buenos aires':  [-34.6037, -58.3816],
    santiago:        [-33.4489, -70.6693],
    lima:            [-12.0464, -77.0428],
    bogota:          [  4.7110, -74.0721],
    caracas:         [ 10.4806, -66.9036],
    quito:           [ -0.1807, -78.4678],
    montevideo:      [-34.9011, -56.1645],
    asuncion:        [-25.2867, -57.6470],
    'la paz':        [-16.5000, -68.1500],
    guayaquil:       [ -2.1710, -79.9224],
    medellin:        [  6.2518, -75.5636],
    cartagena:       [ 10.3997, -75.5144],
    mendoza:         [-32.8908, -68.8272],
    cordoba:         [-31.4201, -64.1888],
    belo:            [-19.9191, -43.9386],
    'belo horizonte':[-19.9191, -43.9386],
    recife:          [ -8.0476, -34.8770],
    salvador:        [-12.9714, -38.5014],
    fortaleza:       [ -3.7172, -38.5434],
    manaus:          [ -3.1190, -60.0217],
    lima2:           [-12.0464, -77.0428],
    paramaribo:      [  5.8520, -55.2038],
    georgetown:      [  6.8013, -58.1551],

    // ── Middle East ─────────────────────────────────────────
    dubai:           [25.2048,  55.2708],
    'abu dhabi':     [24.4539,  54.3773],
    sharjah:         [25.3463,  55.4209],
    riyadh:          [24.6877,  46.7219],
    jeddah:          [21.5433,  39.1728],
    'al khobar':     [26.2172,  50.1971],
    dammam:          [26.4207,  50.0888],
    muscat:          [23.5880,  58.3829],
    doha:            [25.2854,  51.5310],
    kuwait:          [29.3759,  47.9774],
    manama:          [26.2154,  50.5832],
    amman:           [31.9454,  35.9284],
    beirut:          [33.8938,  35.5018],
    baghdad:         [33.3152,  44.3661],
    tehran:          [35.6892,  51.3890],
    isfahan:         [32.6539,  51.6660],
    ankara:          [39.9334,  32.8597],
    istanbul:        [41.0082,  28.9784],
    izmir:           [38.4192,  27.1287],
    'tel aviv':      [32.0853,  34.7818],
    jerusalem:       [31.7683,  35.2137],
    aden:            [12.7797,  45.0368],
    sana:            [15.5527,  32.5324],

    // ── Asia Pacific ────────────────────────────────────────
    tokyo:           [35.6762, 139.6503],
    osaka:           [34.6937, 135.5023],
    kyoto:           [35.0116, 135.7681],
    nagoya:          [35.1815, 136.9066],
    fukuoka:         [33.5904, 130.4017],
    sapporo:         [43.0618, 141.3545],
    beijing:         [39.9042, 116.4074],
    shanghai:        [31.2304, 121.4737],
    guangzhou:       [23.1291, 113.2644],
    shenzhen:        [22.5431, 114.0579],
    chengdu:         [30.5728, 104.0668],
    chongqing:       [29.5630, 106.5516],
    wuhan:           [30.5928, 114.3055],
    xian:            [34.3416, 108.9398],
    tianjin:         [39.3434, 117.3616],
    nanjing:         [32.0603, 118.7969],
    hangzhou:        [30.2741, 120.1551],
    qingdao:         [36.0671, 120.3826],
    'hong kong':     [22.3193, 114.1694],
    macau:           [22.1987, 113.5439],
    singapore:       [ 1.3521, 103.8198],
    'kuala lumpur':  [ 3.1390, 101.6869],
    penang:          [ 5.4141, 100.3288],
    jakarta:         [-6.2088, 106.8456],
    surabaya:        [-7.2575, 112.7521],
    bali:            [-8.3405, 115.0920],
    bangkok:         [13.7563, 100.5018],
    'chiang mai':    [18.7061, 98.9817],
    manila:          [14.5995, 120.9842],
    cebu:            [10.3157, 123.8854],
    hanoi:           [21.0285, 105.8542],
    'ho chi minh':   [10.8231, 106.6297],
    'ho chi minh city':[10.8231,106.6297],
    phnom:           [11.5564, 104.9282],
    'phnom penh':    [11.5564, 104.9282],
    vientiane:       [17.9757, 102.6331],
    yangon:          [16.8661, 96.1951],
    colombo:         [ 6.9271, 79.8612],
    dhaka:           [23.8103, 90.4125],
    karachi:         [24.8607, 67.0011],
    lahore:          [31.5204, 74.3587],
    islamabad:       [33.6844, 73.0479],
    mumbai:          [19.0760, 72.8777],
    delhi:           [28.7041, 77.1025],
    'new delhi':     [28.6139, 77.2090],
    bangalore:       [12.9716, 77.5946],
    bengaluru:       [12.9716, 77.5946],
    hyderabad:       [17.3850, 78.4867],
    chennai:         [13.0827, 80.2707],
    kolkata:         [22.5726, 88.3639],
    pune:            [18.5204, 73.8567],
    ahmedabad:       [23.0225, 72.5714],
    kathmandu:       [27.7172, 85.3240],
    seoul:           [37.5665, 126.9780],
    busan:           [35.1796, 129.0756],
    taipei:          [25.0330, 121.5654],
    'kaohsiung':     [22.6273, 120.3014],
    ulaanbaatar:     [47.8864, 106.9057],
    almaty:          [43.2220,  76.8512],
    tashkent:        [41.2995,  69.2401],
    baku:            [40.4093,  49.8671],
    tbilisi:         [41.6938,  44.8015],
    yerevan:         [40.1872,  44.5152],
    bishkek:         [42.8746,  74.5698],
    dushanbe:        [38.5598,  68.7870],
    ashgabat:        [37.9601,  58.3261],

    // ── Oceania ─────────────────────────────────────────────
    sydney:          [-33.8688, 151.2093],
    melbourne:       [-37.8136, 144.9631],
    brisbane:        [-27.4698, 153.0251],
    perth:           [-31.9505, 115.8605],
    adelaide:        [-34.9285, 138.6007],
    canberra:        [-35.2809, 149.1300],
    'gold coast':    [-28.0167, 153.4000],
    auckland:        [-36.8485, 174.7633],
    wellington:      [-41.2866, 174.7756],
    christchurch:    [-43.5321, 172.6362],
    'suva':          [-18.1416, 178.4415],
    'port moresby':  [ -9.4438, 147.1803],
    noumea:          [-22.2758, 166.4580],
    'papeete':       [-17.5344,-149.5685],
    honolulu:        [21.3069,-157.8583],
  };
  return map[(place ?? '').toLowerCase().trim()] ?? [0, 0];
}

export default function TrackingPage() {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [clearance, setClearance] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [searched, setSearched] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleCards, setVisibleCards] = useState([]);
  const mapRef = useRef(null);

   const [searchParams] = useSearchParams();

  // Pre-fill from URL: /track-order?trackingnumber=AXC-2025-001847
  const urlTracking = searchParams.get('trackingnumber') ?? '';
  const [searchedTracking, setSearchedTracking] = useState(urlTracking);

  // Auto-search immediately if tracking number is in URL
  useEffect(() => {
    if (urlTracking) {
      handleSearch();
    }
  }, []); // runs once on mount

  const currentStepIdx = STEPS.findIndex(
    s => s.label === shipment?.status || s.label.toLowerCase() === (shipment?.status ?? '').toLowerCase()
  );

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!input.trim()) return;
    setLoading(true);
    setNotFound(false);
    setShipment(null);
    setClearance(null);
    setSearched(true);
    setVisibleCards([]);
    try {
      const s = await getShipmentByTracking(input);
      if (!s) { setNotFound(true); return; }
      setShipment(s);
      const fee = await getClearanceFee(s.id);
      setClearance(fee);
      setTimeout(() => setMapReady(true), 600);
      // Staggered card animation
      [0,1,2,3].forEach(i => setTimeout(() => setVisibleCards(p => [...p, i]), 200 + i * 100));
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  // Leaflet map
  useEffect(() => {
    if (!mapReady || !shipment) return;
    const el = document.getElementById('track_map');
    if (!el || el._leaflet_id) return;

    const origin  = geocode(shipment.locations?.origin);
    const current = geocode(shipment.locations?.current);
    const dest    = geocode(shipment.locations?.destination);

    const map = L.map('track_map', {
      dragging: true, touchZoom: true,
      scrollWheelZoom: false, doubleClickZoom: true,
      zoomControl: true,
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '© OpenStreetMap © CARTO',
    }).addTo(map);

    const makeIcon = (color, size = 14) => L.divIcon({
      html: `<div style="
        width:${size}px;height:${size}px;border-radius:50%;
        background:${color};border:3px solid rgba(255,255,255,0.9);
        box-shadow:0 0 0 4px ${color}44, 0 4px 12px rgba(0,0,0,.4);
      "></div>`,
      className: '', iconSize: [size, size], iconAnchor: [size/2, size/2],
    });

    L.marker(origin, { icon: makeIcon('#94a3b8', 12) })
      .addTo(map).bindPopup(`<b>Origin</b><br>${shipment.locations?.origin}`);
    L.marker(current, { icon: makeIcon('#f59e0b', 18) })
      .addTo(map).bindPopup(`<b>Current Location</b><br>${shipment.locations?.current}`);
    L.marker(dest, { icon: makeIcon('#10b981', 16) })
      .addTo(map).bindPopup(`<b>Destination</b><br>${shipment.locations?.destination}`);

    // Dashed route line
    L.polyline([origin, current, dest], {
      color: '#f59e0b', weight: 3, opacity: 0.6,
      dashArray: '8, 8',
    }).addTo(map);

    // Solid completed route
    L.polyline([origin, current], {
      color: '#f59e0b', weight: 4, opacity: 0.9,
    }).addTo(map);

    const bounds = L.latLngBounds([origin, current, dest]);
    map.fitBounds(bounds, { padding: [50, 50] });
    mapRef.current = map;

    return () => { map.remove(); mapRef.current = null; };
  }, [mapReady, shipment]);

  const statusColor = STATUS_COLORS[shipment?.status] ?? '#6366f1';

  return (
    <div className="min-h-screen bg-[#020818] text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;600&display=swap');

        :root {
          --navy: #020818;
          --navy2: #0a1628;
          --navy3: #0f2040;
          --gold: #f59e0b;
          --gold2: #fbbf24;
          --slate: #94a3b8;
          --card: rgba(15, 32, 64, 0.8);
        }

        * { box-sizing: border-box; }

        .card-enter {
          animation: cardIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes cardIn {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .pulse-ring {
          animation: pulseRing 2s ease-out infinite;
        }
        @keyframes pulseRing {
          0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(245,158,11,0.4); }
          70%  { transform: scale(1);    box-shadow: 0 0 0 16px rgba(245,158,11,0); }
          100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(245,158,11,0); }
        }

        .shimmer {
          background: linear-gradient(90deg, #0f2040 25%, #1a3060 50%, #0f2040 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .step-line {
          background: linear-gradient(90deg, var(--gold) var(--pct), rgba(255,255,255,0.1) var(--pct));
        }

        input::placeholder { color: rgba(148,163,184,0.5); }
        input:focus { outline: none; }

        /* Leaflet dark overrides */
        .leaflet-container { background: #020818 !important; }
        .leaflet-popup-content-wrapper {
          background: #0f2040 !important;
          color: white !important;
          border: 1px solid rgba(245,158,11,0.3) !important;
          border-radius: 12px !important;
        }
        .leaflet-popup-tip { background: #0f2040 !important; }

        @media (max-width: 640px) {
          .hero-title { font-size: 2rem !important; }
        }
      `}</style>

      {/* ── HEADER ── */}
      <header className="sticky top-0 z-50 border-b border-white/5" style={{ background: 'rgba(2,8,24,0.95)', backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <a href="/" className="flex items-center gap-3">
              <img
                src="https://Atlasroutelogistics.com/storage/app/public/photos/logo/newlogo.png"
                alt="AlignX Cargo"
                className="h-9 md:h-11 w-auto brightness-0 invert"
                onError={e => { e.target.style.display='none'; }}
              />
              <span className="text-lg font-bold hidden sm:block">
                <span style={{ color: 'var(--gold)' }}>ATLASROUTELOGISTICS</span>
              </span>
            </a>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-8">
              {[['/', 'Home'], ['/about', 'About'], ['/services', 'Services'], ['/track-order', 'Track'], ['/contact', 'Contact']].map(([href, label]) => (
                <a key={href} href={href}
                  className="text-sm font-medium transition-colors hover:text-yellow-400"
                  style={{ color: href === '/track-order' ? 'var(--gold)' : 'var(--slate)' }}
                >{label}</a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a href="/contact"
                className="px-5 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-105"
                style={{ background: 'var(--gold)', color: '#020818' }}
              >Get Quote</a>
            </div>

            {/* Mobile menu toggle */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 rounded-lg" style={{ color: 'var(--slate)' }}>
              <div className="space-y-1.5">
                <div className={`w-6 h-0.5 bg-current transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`} />
                <div className={`w-6 h-0.5 bg-current transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`} />
                <div className={`w-6 h-0.5 bg-current transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/5 px-4 py-4 space-y-1" style={{ background: 'var(--navy2)' }}>
            {[['/', 'Home'], ['/about', 'About'], ['/services', 'Services'], ['/track-order', 'Track Shipment'], ['/contact', 'Contact']].map(([href, label]) => (
              <a key={href} href={href} className="block px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors" style={{ color: 'var(--slate)' }}>{label}</a>
            ))}
            <div className="pt-2">
              <a href="/contact" className="block text-center px-4 py-2.5 rounded-xl text-sm font-bold" style={{ background: 'var(--gold)', color: '#020818' }}>Get Quote</a>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO / SEARCH ── */}
      <section className="relative overflow-hidden py-16 md:py-24">
        {/* Background grid */}
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'linear-gradient(rgba(245,158,11,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.15) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }} />
        {/* Radial glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full opacity-20 blur-3xl pointer-events-none"
          style={{ background: 'radial-gradient(circle, #f59e0b 0%, transparent 70%)' }} />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          {/* Top pill */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-semibold mb-6"
            style={{ borderColor: 'rgba(245,158,11,0.3)', color: 'var(--gold)', background: 'rgba(245,158,11,0.08)' }}>
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
            Real-time Global Tracking
          </div>

          <h1 className="hero-title text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-4" style={{ letterSpacing: '-0.02em' }}>
            Where is your
            <span style={{ color: 'var(--gold)' }}> shipment</span>
            <br />right now?
          </h1>
          <p className="text-base md:text-lg mb-10" style={{ color: 'var(--slate)' }}>
            Enter your tracking number to get a live update on your package's journey.
          </p>

          {/* Search box */}
          <form onSubmit={handleSearch}>
            <div className="flex flex-col sm:flex-row gap-3 p-2 rounded-2xl border"
              style={{ background: 'var(--navy2)', borderColor: 'rgba(245,158,11,0.2)' }}>
              <div className="flex-1 flex items-center gap-3 px-4">
                <svg className="w-5 h-5 shrink-0" style={{ color: 'var(--gold)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="e.g. AXC-1234567890"
                  className="w-full bg-transparent text-white text-base font-medium py-3"
                  style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.05em' }}
                />
                {input && (
                  <button type="button" onClick={() => setInput('')} className="shrink-0 text-slate-500 hover:text-white transition-colors">✕</button>
                )}
              </div>
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="sm:shrink-0 px-7 py-3 rounded-xl font-bold text-sm transition-all hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                style={{ background: 'var(--gold)', color: '#020818' }}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Tracking…
                  </span>
                ) : 'Track Now'}
              </button>
            </div>
          </form>

          {/* Loading skeleton */}
          {loading && (
            <div className="mt-10 space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 rounded-2xl shimmer" />
              ))}
            </div>
          )}

          {/* Not found */}
          {notFound && !loading && (
            <div className="mt-10 p-8 rounded-2xl border text-center card-enter"
              style={{ background: 'var(--navy2)', borderColor: 'rgba(239,68,68,0.3)' }}>
              <div className="text-5xl mb-4">📭</div>
              <h3 className="text-xl font-bold mb-2">No shipment found</h3>
              <p style={{ color: 'var(--slate)' }}>
                No shipment matches <span className="font-mono text-yellow-400">"{input}"</span>. Please check your tracking number and try again.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* ── RESULTS ── */}
      {shipment && !loading && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 space-y-6">

          {/* ── Summary banner ── */}
          <div className="rounded-3xl overflow-hidden card-enter" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="px-6 md:px-10 py-8" style={{ background: `linear-gradient(135deg, ${statusColor}22 0%, transparent 60%)` }}>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--slate)' }}>Tracking Number</p>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="text-3xl md:text-4xl font-black" style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--gold)' }}>
                      {shipment.trackingNumber}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)' }}>
                      ✓ Verified
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-start md:items-end gap-3">
                  <div className="flex items-center gap-3 px-5 py-3 rounded-2xl" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="w-3 h-3 rounded-full pulse-ring" style={{ background: statusColor }} />
                    <span className="text-sm font-semibold">{shipment.status}</span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--slate)' }}>
                    Last updated: {shipment.lastUpdate}
                  </p>
                </div>
              </div>
            </div>

            {/* Info cards grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: 'rgba(255,255,255,0.04)' }}>
              {[
                {
                  idx: 0, icon: '👤', title: 'Sender',
                  rows: [['Name', shipment.sender?.name], ['Address', shipment.sender?.address], ['Phone', shipment.sender?.phone]],
                },
                {
                  idx: 1, icon: '📬', title: 'Receiver',
                  rows: [['Name', shipment.receiver?.name], ['Address', shipment.receiver?.address], ['Email', shipment.receiver?.email]],
                },
                {
                  idx: 2, icon: '📦', title: 'Shipment',
                  rows: [['Weight', shipment.details?.weight], ['Type', shipment.details?.type], ['Shipped', shipment.details?.shipped]],
                },
                {
                  idx: 3, icon: '📍', title: 'Route',
                  rows: [['Origin', shipment.locations?.origin], ['Current', shipment.locations?.current], ['Destination', shipment.locations?.destination]],
                },
              ].map(card => (
                <div key={card.idx}
                  className={`p-6 ${visibleCards.includes(card.idx) ? 'card-enter' : 'opacity-0'}`}
                  style={{ background: 'var(--navy2)', animationDelay: `${card.idx * 0.1}s` }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-lg">{card.icon}</span>
                    <span className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--gold)' }}>{card.title}</span>
                  </div>
                  <div className="space-y-2">
                    {card.rows.map(([k, v]) => v ? (
                      <div key={k}>
                        <p className="text-xs mb-0.5" style={{ color: 'var(--slate)' }}>{k}</p>
                        <p className="text-sm font-medium text-white leading-tight">{v}</p>
                      </div>
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Progress tracker ── */}
          <div className="rounded-3xl p-6 md:p-10 card-enter" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <h3 className="text-lg font-bold mb-8">Shipment Progress</h3>

            {/* Desktop progress */}
            <div className="hidden md:block relative">
              <div className="absolute top-6 left-6 right-6 h-0.5" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <div
                  className="h-full transition-all duration-1000"
                  style={{
                    width: `${Math.max(0, currentStepIdx) / (STEPS.length - 1) * 100}%`,
                    background: `linear-gradient(90deg, var(--gold), ${statusColor})`,
                  }}
                />
              </div>
              <div className="relative flex justify-between">
                {STEPS.map((step, i) => {
                  const done = i < currentStepIdx;
                  const active = i === currentStepIdx;
                  return (
                    <div key={step.label} className="flex flex-col items-center" style={{ width: `${100/STEPS.length}%` }}>
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center text-lg border-2 transition-all ${active ? 'pulse-ring' : ''}`}
                        style={{
                          background: done || active ? `${statusColor}22` : 'rgba(255,255,255,0.04)',
                          borderColor: done || active ? statusColor : 'rgba(255,255,255,0.1)',
                          color: done || active ? statusColor : 'rgba(255,255,255,0.3)',
                        }}
                      >
                        {done ? '✓' : step.icon}
                      </div>
                      <p className="mt-3 text-xs font-medium text-center leading-tight px-1"
                        style={{ color: done || active ? 'white' : 'var(--slate)' }}>
                        {step.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile progress */}
            <div className="md:hidden space-y-3">
              {STEPS.map((step, i) => {
                const done = i < currentStepIdx;
                const active = i === currentStepIdx;
                return (
                  <div key={step.label} className="flex items-center gap-4 p-3 rounded-xl"
                    style={{ background: active ? `${statusColor}11` : 'transparent', border: active ? `1px solid ${statusColor}33` : '1px solid transparent' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-base shrink-0"
                      style={{
                        background: done || active ? `${statusColor}22` : 'rgba(255,255,255,0.04)',
                        border: `2px solid ${done || active ? statusColor : 'rgba(255,255,255,0.1)'}`,
                        color: done || active ? statusColor : 'rgba(255,255,255,0.3)',
                      }}>
                      {done ? '✓' : step.icon}
                    </div>
                    <span className="text-sm font-medium" style={{ color: done || active ? 'white' : 'var(--slate)' }}>
                      {step.label}
                    </span>
                    {active && <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: `${statusColor}22`, color: statusColor }}>Current</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── History + Map ── */}
          <div className="grid lg:grid-cols-5 gap-6">
            {/* History */}
            <div className="lg:col-span-2 rounded-3xl p-6 md:p-8 card-enter" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <h3 className="text-lg font-bold mb-6 flex items-center gap-2">
                <span style={{ color: 'var(--gold)' }}>📋</span> Shipment History
              </h3>
              {(shipment.history ?? []).length === 0 ? (
                <p className="text-sm italic" style={{ color: 'var(--slate)' }}>No history entries yet.</p>
              ) : (
                <div className="relative space-y-5">
                  <div className="absolute left-4 top-2 bottom-2 w-px" style={{ background: 'linear-gradient(to bottom, var(--gold), transparent)' }} />
                  {shipment.history.map((entry, i) => (
                    <div key={i} className="flex gap-4 pl-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10"
                        style={{ background: i === 0 ? statusColor : 'rgba(255,255,255,0.06)', border: '2px solid rgba(255,255,255,0.1)' }}>
                        <span className="text-xs">📍</span>
                      </div>
                      <div className="flex-1 pb-2">
                        <p className="text-xs mb-0.5" style={{ color: 'var(--slate)' }}>{entry.time}</p>
                        <p className="text-sm font-semibold">{entry.status} <span style={{ color: 'var(--gold)' }}>·</span> {entry.loc}</p>
                        {entry.note && <p className="text-xs mt-0.5" style={{ color: 'var(--slate)' }}>{entry.note}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Map */}
            <div className="lg:col-span-3 rounded-3xl overflow-hidden card-enter" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="px-6 py-4 flex items-center justify-between border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <span style={{ color: 'var(--gold)' }}>🗺️</span> Live Route
                </h3>
                <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--slate)' }}>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-400" />Origin</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-yellow-400" />Current</span>
                  <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400" />Dest.</span>
                </div>
              </div>
              <div className="h-[380px] md:h-[420px] relative">
                <div id="track_map" className="absolute inset-0" />
              </div>
            </div>
          </div>

          {/* ── Parcel info ── */}
          <div className="rounded-3xl overflow-hidden card-enter" style={{ background: 'var(--navy2)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="px-6 md:px-10 py-6 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <span style={{ color: 'var(--gold)' }}>📦</span> Parcel Details
              </h3>
            </div>
            <div className="p-6 md:p-10 grid md:grid-cols-2 gap-8 items-start">
              {shipment.parcelImageUrl && (
                <div className="rounded-2xl overflow-hidden border" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <img src={shipment.parcelImageUrl} alt="Parcel" className="w-full object-cover" style={{ maxHeight: '280px' }} />
                </div>
              )}
              <div>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {[
                    ['Weight', shipment.details?.weight, '⚖️'],
                    ['Type', shipment.details?.type, '🚚'],
                    ['Shipped', shipment.details?.shipped, '📅'],
                    ['Status', shipment.status, '🔄'],
                  ].map(([k, v, icon]) => (
                    <div key={k} className="p-4 rounded-2xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-xs mb-1" style={{ color: 'var(--slate)' }}>{icon} {k}</p>
                      <p className="text-sm font-bold" style={{ color: k === 'Status' ? statusColor : 'white' }}>{v}</p>
                    </div>
                  ))}
                </div>
                {shipment.description && (
                  <div className="p-4 rounded-2xl mb-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--slate)' }}>📝 Description</p>
                    <p className="text-sm">{shipment.description}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Clearance Fee CTA ── */}
          {clearance && !clearance.isPaid && (
            <div className="rounded-3xl p-6 md:p-10 card-enter" style={{
              background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(245,158,11,0.08) 100%)',
              border: '1px solid rgba(239,68,68,0.25)',
            }}>
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">⚠️</span>
                    <h3 className="text-xl font-black">{clearance.feeLabel}</h3>
                  </div>
                  <p className="text-3xl font-black mb-1" style={{ color: 'var(--gold)' }}>
                    {clearance.amount} <span className="text-xl">{clearance.currency}</span>
                  </p>
                  <p style={{ color: 'var(--slate)' }} className="text-sm">
                    Your shipment is on hold pending clearance payment.
                    {clearance.dueDate && <span className="ml-2">Due by <strong className="text-white">{clearance.dueDate}</strong>.</span>}
                  </p>
                  {clearance.notes && <p className="text-sm mt-1" style={{ color: 'var(--slate)' }}>{clearance.notes}</p>}
                </div>
                <button
                  onClick={() => navigate('/payment', { state: { shipment, clearance } })}
                  className="shrink-0 flex items-center gap-3 px-8 py-4 rounded-2xl font-black text-base transition-all hover:scale-105 hover:shadow-lg"
                  style={{ background: 'var(--gold)', color: '#020818', boxShadow: '0 0 40px rgba(245,158,11,0.3)' }}
                >
                  <span>💳</span> Pay Clearance Fee
                </button>
              </div>
            </div>
          )}

          {clearance?.isPaid && (
            <div className="rounded-3xl p-6 card-enter flex items-center gap-4"
              style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
              <span className="text-3xl">✅</span>
              <div>
                <p className="font-bold text-emerald-400">Clearance Fee Paid</p>
                <p className="text-sm" style={{ color: 'var(--slate)' }}>Payment for {clearance.feeLabel} has been verified.</p>
              </div>
            </div>
          )}

          {/* Print button */}
          <div className="flex justify-end">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors hover:bg-white/5"
              style={{ borderColor: 'rgba(255,255,255,0.1)', color: 'var(--slate)' }}
            >
              🖨️ Print Receipt
            </button>
          </div>
        </div>
      )}

      {/* ── FOOTER ── */}
      <footer className="border-t mt-10 py-10" style={{ borderColor: 'rgba(255,255,255,0.06)', background: 'var(--navy2)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <img
            src="https://Atlasroutelogistics.com/storage/app/public/photos/logo/newlogo.png"
            alt="ATLASROUTELOGISTICS" className="h-10 mx-auto mb-4 brightness-0 invert opacity-60"
            onError={e => e.target.style.display = 'none'}
          />
          <p className="text-sm" style={{ color: 'var(--slate)' }}>
            © {new Date().getFullYear()} ATLASROUTELOGISTICS. All rights reserved. · Open 24/7 ·{' '}
            <a href="mailto:atlasroutelogistics@kvm3.com" className="hover:text-yellow-400 transition-colors">atlasroutelogistics@kvm3.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
}